import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader, Empty } from "../components/Bits";
import TransInput from "../components/TransInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowRight, Zap, Trash2, ChevronUp, ChevronDown, Pencil } from "lucide-react";

const FIELD_TYPES = ["text", "number", "date", "time", "select", "project", "employee", "work_group", "subgroup", "work", "quantity", "unit", "comment", "photo", "file", "signature", "gps", "checkbox"];
const TRIGGERS = ["report.submitted", "report.not_submitted", "report.rejected", "report.approved", "worker.absent", "hours.exceed_norm", "project.behind", "project.assigned", "role.changed"];
const ACTIONS = ["notify", "email", "push", "sms"];

export default function Constructor({ kind }) {
  const { t, tn } = useI18n();
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(false);
  const [item, setItem] = useState(null);
  const endpoint = kind === "forms" ? "/forms" : kind === "workflows" ? "/workflows" : "/notification-rules";
  const title = kind === "forms" ? t("forms") : kind === "workflows" ? t("workflows") : t("automations");
  const roleKeys = roles.map((r) => r.key);
  const roleName = (k) => tn(roles.find((r) => r.key === k)?.name) || k;

  const load = () => api.get(endpoint).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)); }, [kind]);

  const open = (row) => {
    if (row) setItem(JSON.parse(JSON.stringify(row)));
    else if (kind === "forms") setItem({ name: "", fields: [], roles: [], active: true });
    else if (kind === "workflows") setItem({ name: "", steps: [], active: true });
    else setItem({ name: "", trigger: TRIGGERS[0], target_role: "foreman", action: "notify", active: true });
    setModal(true);
  };
  const save = async () => {
    try {
      if (item.id) await api.put(`${endpoint}/${item.id}`, item); else await api.post(endpoint, item);
      toast.success(t("save")); setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const del = async (id) => { await api.delete(`${endpoint}/${id}`); load(); };
  const move = (key, i, d) => {
    const arr = [...item[key]]; const j = i + d; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setItem({ ...item, [key]: arr });
  };
  const updField = (i, patch) => setItem({ ...item, fields: item.fields.map((f, j) => j === i ? { ...f, ...patch } : f) });

  return (
    <div>
      <PageHeader title={title} subtitle={t("visual_constructor")}
        action={<Button data-testid="add-constructor-btn" onClick={() => open(null)} className="bg-[var(--amber)] text-white gap-2"><Plus size={16} /> {t("create")}</Button>} />
      {rows.length === 0 ? <Empty /> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`constructor-${r.id}`} className="tactical-card p-4 kpi-accent">
              <div className="flex items-start gap-2">
                <div className="font-display font-bold flex-1">{r.name}</div>
                <button onClick={() => open(r)} className="p-1 text-[var(--blue)]" data-testid={`edit-${r.id}`}><Pencil size={15} /></button>
                <button onClick={() => del(r.id)} className="p-1 text-[var(--red)]" data-testid={`delete-${r.id}`}><Trash2 size={15} /></button>
              </div>
              {kind === "forms" && <div className="text-sm text-[var(--text-3)] mt-1">{(r.fields || []).length} {t("fields").toLowerCase()} · {(r.roles || []).map(roleName).join(", ") || t("total")}{r.active === false && <span className="ml-2 text-[var(--red)]">off</span>}</div>}
              {kind === "workflows" && <div className="flex items-center gap-1 flex-wrap mt-2 text-xs">
                <span className="bg-[var(--bg)] px-2 py-0.5 rounded">{roleName("worker")}</span>
                {(r.steps || []).map((s, i) => <span key={i} className="flex items-center gap-1"><ArrowRight size={12} /><span className="bg-[var(--bg)] px-2 py-0.5 rounded">{roleName(s.role || s)}</span></span>)}
                {r.active === false && <span className="ml-2 text-[var(--red)]">off</span>}
              </div>}
              {kind === "automations" && <div className="text-xs mt-2 font-mono-num"><Zap size={12} className="inline text-[var(--amber)]" /> IF {r.trigger} → {r.action || "notify"}({roleName(r.target_role)})</div>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{item?.id ? t("edit") : t("create")} — {title}</DialogTitle></DialogHeader>
          {item && (
            <div className="space-y-3">
              <Input placeholder={t("name")} value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} data-testid="constructor-name" />

              {kind === "forms" && (
                <div>
                  <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-1">{t("form_roles")}</div>
                  <div className="flex flex-wrap gap-2 mb-3 text-xs">
                    {roleKeys.map((rk) => <label key={rk} className="flex items-center gap-1 tactical-card px-2 py-1"><Checkbox checked={(item.roles || []).includes(rk)} onCheckedChange={(c) => setItem({ ...item, roles: c ? [...(item.roles || []), rk] : (item.roles || []).filter((x) => x !== rk) })} data-testid={`form-role-${rk}`} /> {roleName(rk)}</label>)}
                    <label className="flex items-center gap-1 tactical-card px-2 py-1 ml-auto"><Checkbox checked={item.active !== false} onCheckedChange={(c) => setItem({ ...item, active: !!c })} data-testid="form-active" /> {t("active")}</label>
                  </div>
                  <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-2">{t("fields")}</div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {FIELD_TYPES.map((ft) => (
                      <button key={ft} data-testid={`field-${ft}`} onClick={() => setItem({ ...item, fields: [...item.fields, { id: `f_${Date.now().toString(36)}`, type: ft, label: { en: ft }, required: false, visible: true, editable_roles: [] }] })}
                        className="text-xs tactical-card px-2 py-1 hover:border-[var(--amber)]">+ {ft}</button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {item.fields.map((f, i) => (
                      <div key={i} className="tactical-card p-2 text-sm space-y-2" data-testid={`form-field-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono-num text-xs bg-[var(--bg)] px-2 py-0.5 rounded">{f.type}</span>
                          <span className="flex-1" />
                          <button onClick={() => move("fields", i, -1)} className="p-1"><ChevronUp size={14} /></button>
                          <button onClick={() => move("fields", i, 1)} className="p-1"><ChevronDown size={14} /></button>
                          <button onClick={() => setItem({ ...item, fields: item.fields.filter((_, j) => j !== i) })} className="p-1 text-[var(--red)]"><Trash2 size={14} /></button>
                        </div>
                        <TransInput value={typeof f.label === "string" ? { en: f.label } : f.label} onChange={(v) => updField(i, { label: v })} placeholder={t("label")} />
                        {f.type === "select" && <Input className="h-8 text-xs" placeholder={t("options")} value={(f.options || []).join(", ")} onChange={(e) => updField(i, { options: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid={`field-options-${i}`} />}
                        <div className="flex flex-wrap items-center gap-3 text-xs">
                          <label className="flex items-center gap-1"><Checkbox checked={!!f.required} onCheckedChange={(c) => updField(i, { required: !!c })} data-testid={`field-req-${i}`} /> {t("required")}</label>
                          <label className="flex items-center gap-1"><Checkbox checked={f.visible !== false} onCheckedChange={(c) => updField(i, { visible: !!c })} /> {t("visible")}</label>
                          <span className="text-[var(--text-3)]">{t("editable_by")}:</span>
                          {roleKeys.map((rk) => <label key={rk} className="flex items-center gap-1"><Checkbox checked={(f.editable_roles || []).includes(rk)} onCheckedChange={(c) => updField(i, { editable_roles: c ? [...(f.editable_roles || []), rk] : (f.editable_roles || []).filter((x) => x !== rk) })} /> {roleName(rk)}</label>)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {kind === "workflows" && (
                <div>
                  <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-2">{t("steps")}</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {roleKeys.filter((r) => r !== "worker").map((r) => <button key={r} data-testid={`step-${r}`} onClick={() => setItem({ ...item, steps: [...item.steps, { role: r }] })} className="text-xs tactical-card px-2 py-1 hover:border-[var(--amber)]">+ {roleName(r)}</button>)}
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs bg-[var(--bg)] px-2 py-1.5 rounded inline-block">{roleName("worker")} ({t("submit")})</div>
                    {item.steps.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 tactical-card px-2 py-1.5 text-sm" data-testid={`wf-step-${i}`}>
                        <ArrowRight size={14} className="text-[var(--amber)]" /> <span className="flex-1 font-semibold">{i + 1}. {roleName(s.role)}</span>
                        <button onClick={() => move("steps", i, -1)} className="p-1"><ChevronUp size={14} /></button>
                        <button onClick={() => move("steps", i, 1)} className="p-1"><ChevronDown size={14} /></button>
                        <button onClick={() => setItem({ ...item, steps: item.steps.filter((_, j) => j !== i) })} className="p-1 text-[var(--red)]"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-sm mt-3"><Checkbox checked={item.active !== false} onCheckedChange={(c) => setItem({ ...item, active: !!c })} data-testid="wf-active" /> {t("active")}</label>
                </div>
              )}

              {kind === "automations" && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-[var(--text-3)]">IF</div>
                  <select className="border rounded-md h-10 px-2 text-sm w-full bg-[var(--card)]" value={item.trigger} onChange={(e) => setItem({ ...item, trigger: e.target.value })} data-testid="rule-trigger">
                    {TRIGGERS.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                  <div className="text-xs font-bold text-[var(--text-3)]">THEN</div>
                  <div className="grid grid-cols-2 gap-2">
                    <select className="border rounded-md h-10 px-2 text-sm w-full bg-[var(--card)]" value={item.action || "notify"} onChange={(e) => setItem({ ...item, action: e.target.value })} data-testid="rule-action">
                      {ACTIONS.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                    <select className="border rounded-md h-10 px-2 text-sm w-full bg-[var(--card)]" value={item.target_role} onChange={(e) => setItem({ ...item, target_role: e.target.value })} data-testid="rule-target">
                      {roleKeys.map((x) => <option key={x} value={x}>{roleName(x)}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>{t("cancel")}</Button>
            <Button onClick={save} className="bg-[var(--amber)] text-white" data-testid="constructor-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
